import { defineStore } from 'pinia';
import { wordpressApi } from '../api/wordpress.js';
import { processPlate } from '../utils/licensePlate.js';
import { trackRequestStarted, trackRequestCompleted } from '@/js/gtm-tracking.js';

// Monotonic id to guard against out-of-order calculate() responses: a slow
// earlier request must not overwrite the result of a newer one. Kept outside
// Pinia state so it carries no reactivity overhead.
let calculateRequestId = 0;

export const useFormStore = defineStore( 'form', {
	state: () => ( {
		carId: null,
		vehicleUlid: null,
		// Vehicle payload inlined into the page (data-vehicle attribute) so the
		// detail page needn't make a second browser round-trip for data it
		// already rendered server-side. Same shape as the REST `data` object.
		initialVehicleData: null,
		// Step 1 data - calculation fields
		type: 0, // 0 = Zakelijk (always)
		submissionMode: 'lease', // 'lease' | 'koop'
		looptijd: 72,
		aanbetaling: null, // null or number
		slottermijn: null, // null or number
		inruilen: false,
		kenteken: '', // License plate
		kentekenInputMode: 'search', // 'search' | 'free'
		// Step 2+ data (personal info)
		naam: '',
		email: '',
		telefoonnummer: '',
		bedrijfsnaam: '',
		// Calculated values
		maandbedrag: 0,
		aankoopbedrag: 0,
		btwBedrag: 0,
		btwStatus: 'ex', // 'in', 'ex', or 'Marge'
		// Car meta for summaries
		carTitle: '',
		carImageUrl: '',
		carImageSummaryUrl: '',
		carTags: [],
		// Lease data availability
		hasLeaseData: false,
		// Step completion tracking
		completedSteps: [],
		// Loading states
		isReady: false,
		isSubmitting: false,
		isSubmitted: false,
		isPriceUpdating: false,
		isAankoopbedragUpdating: false,
		// Errors
		errors: {},
	} ),

	getters: {
		/**
		 * Check if step 1 is valid (all required fields filled)
		 * Validation rules will be added later
		 */
		isStep1Valid( state ) {
			const hasAll = state.type !== null && state.looptijd !== null && state.aanbetaling !== null && state.slottermijn !== null;
			if ( ! hasAll ) {
				return false;
			}
			// Business rules
			if ( ! this.hasMinimumLeasebedrag ) {
				return false;
			}
			if ( ! this.hasValidAanbetaling ) {
				return false;
			}
			if ( ! this.hasValidSlottermijn ) {
				return false;
			}
			// License plate validation: if trade-in is enabled and plate is entered, it must be valid
			if ( ! this.hasValidLicensePlate ) {
				return false;
			}
			return true;
		},

		/**
		 * Check if step 1 is completed
		 */
		isStep1Completed( state ) {
			return state.completedSteps.includes( 1 );
		},

		/**
		 * Afgeleide bedragen
		 */
		leasebedrag( state ) {
			const aanbetaling = state.aanbetaling === null ? 0 : Number( state.aanbetaling );
			return Math.max( 0, Number( state.aankoopbedrag ) - aanbetaling );
		},

		/**
		 * Maximum aanbetaling (aankoopbedrag - €4.000)
		 */
		maxDownpayment() {
			const aankoopbedrag = this.aankoopbedrag || 0;
			return Math.max( 0, aankoopbedrag - 4000 );
		},

		/**
		 * Validatie regels (frontend)
		 */
		hasMinimumLeasebedrag() {
			return this.leasebedrag >= 2500;
		},
		hasValidAanbetaling() {
			const aanbetaling = this.aanbetaling === null ? 0 : Number( this.aanbetaling );
			return aanbetaling <= this.maxDownpayment;
		},
		hasValidSlottermijn() {
			const slottermijn = this.slottermijn === null ? 0 : Number( this.slottermijn );
			return slottermijn <= this.leasebedrag;
		},
		hasValidLicensePlate() {
			// License plate is optional, but if trade-in is enabled and a plate is entered, it must be valid
			if ( ! this.inruilen ) {
				return true; // No validation needed if trade-in is disabled
			}
			// Skip validation in free input mode
			if ( this.kentekenInputMode === 'free' ) {
				return true;
			}
			// If trade-in is enabled but no plate is entered, that's OK (optional)
			const cleanedPlate = ( this.kenteken || '' ).trim();
			if ( cleanedPlate === '' ) {
				return true;
			}
			// Dutch license plates are 6 or 7 characters
			// Only validate if plate has complete length (6 or 7 chars)
			// Allow shorter plates (user might still be typing)
			if ( cleanedPlate.length < 6 ) {
				return true; // Don't block navigation while user is still typing
			}
			// If plate has 6 or 7 characters, it must be valid
			const processed = processPlate( this.kenteken );
			return processed.isValid && processed.sidecode !== null;
		},
		step1ValidationErrors() {
			const errors = {};
			// Only show license plate error if trade-in is enabled and plate is invalid
			if ( this.inruilen ) {
				// Skip validation in free input mode
				if ( this.kentekenInputMode !== 'free' ) {
					const cleanedPlate = ( this.kenteken || '' ).trim();
					// Only show error for complete-looking plates (6 or 7 chars) that are invalid
					if ( cleanedPlate !== '' && cleanedPlate.length >= 6 ) {
						const processed = processPlate( this.kenteken );
						if ( ! processed.isValid || processed.sidecode === null ) {
							errors.kenteken = 'Voer een geldig kenteken in';
						}
					}
				}
			}
			return errors;
		},

		/**
		 * Get formatted monthly payment
		 */
		formattedMaandbedrag( state ) {
			return new Intl.NumberFormat( 'nl-NL', {
				style: 'currency',
				currency: 'EUR',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			} ).format( state.maandbedrag );
		},

		/**
		 * Get formatted maximum downpayment (for error messages)
		 */
		formattedMaxDownpayment() {
			const isWholeNumber = Number.isInteger( this.maxDownpayment );
			return new Intl.NumberFormat( 'nl-NL', {
				style: 'currency',
				currency: 'EUR',
				minimumFractionDigits: isWholeNumber ? 0 : 2,
				maximumFractionDigits: isWholeNumber ? 0 : 2,
			} ).format( this.maxDownpayment );
		},

		/**
		 * Step 2 validation
		 */
		isStep2Valid( state ) {
			const errors = this.step2ValidationErrors;
			return Object.keys( errors ).length === 0;
		},
		step2ValidationErrors( state ) {
			const errors = {};
			const email = ( state.email || '' ).trim();
			const name = ( state.naam || '' ).trim();
			if ( email === '' ) {
				errors.email = 'E-mailadres is verplicht';
			} else if ( ! /.+@.+\..+/.test( email ) ) {
				errors.email = 'Voer een geldig e-mailadres in';
			}
			if ( name === '' ) {
				errors.fullName = 'Naam is verplicht';
			}
			// Bedrijfsnaam verplicht bij zakelijk (type 0)
			if ( state.type === 0 ) {
				const companyName = ( state.bedrijfsnaam || '' ).trim();
				if ( companyName === '' ) {
					errors.companyName = 'Bedrijfsnaam is verplicht';
				}
			}
		const phone = ( state.telefoonnummer || '' ).trim();
		if ( phone === '' ) {
			errors.phone = 'Telefoonnummer is verplicht';
		} else {
			const digits = phone.replace( /\D/g, '' );
			const digitLength = digits.length;
			// Nederlandse nummers: 10 cijfers (binnenlands met 0) of 11 cijfers (internationaal met 31)
			if ( digitLength === 0 || digitLength < 10 || digitLength > 11 ) {
				errors.phone = 'Voer een geldig telefoonnummer in';
			} else if ( digitLength === 10 && ! digits.startsWith( '0' ) ) {
				// Binnenlands nummer moet starten met 0
				errors.phone = 'Voer een geldig telefoonnummer in';
			} else if ( digitLength === 11 && ! digits.startsWith( '31' ) ) {
				// Internationaal nummer moet starten met 31
				errors.phone = 'Voer een geldig telefoonnummer in';
			}
		}
			return errors;
		},
	},

	actions: {
			/**
			 * Load UTM data from sessionStorage (set by standalone UTM tracking script)
			 */
			loadUtmFromStorage() {
				if ( typeof window.grevenUtmTracking !== 'undefined' ) {
					const utmData = window.grevenUtmTracking.getUtmData();
					return {
						utm: utmData.utm || [],
						gclid: utmData.gclid || null,
						fbclid: utmData.fbclid || null,
					};
				}
				return {
					utm: [],
					gclid: null,
					fbclid: null,
				};
			},

			/**
			 * Build submission payload combining Step 1 and Step 2
			 */
			getSubmissionPayload() {
				// Load UTM data from sessionStorage
				const utmData = this.loadUtmFromStorage();

				const payload = {
					car_id: this.carId || 0,
					form_data: {
						step1: {
							submissionMode: this.submissionMode,
							type: this.type,
							looptijd: this.looptijd,
							aanbetaling: this.aanbetaling,
							slottermijn: this.slottermijn,
							inruilen: this.inruilen,
							kenteken: this.kenteken,
						},
						step2: {
							companyName: this.bedrijfsnaam,
							fullName: this.naam,
							email: this.email,
							phone: this.telefoonnummer,
							kenteken: this.kenteken,
							inruilen: this.inruilen,
						},
					// Add UTM tracking data
					utm: utmData.utm,
					gclid: utmData.gclid,
					fbclid: utmData.fbclid,
				},
			};

				if ( this.vehicleUlid ) {
					payload.vehicle_ulid = this.vehicleUlid;
					payload.form_data.vehicle_ulid = this.vehicleUlid;
				}

				return payload;
		},
		/**
		 * Set car ID
		 */
		setCarId( carId ) {
			this.carId = carId;
		},

		/**
		 * Set aanbod-new vehicle ULID
		 */
		setVehicleUlid( vehicleUlid ) {
			this.vehicleUlid = vehicleUlid;
		},

		/**
		 * Seed the vehicle payload inlined into the page so fetchCarData() can
		 * skip the REST round-trip.
		 *
		 * @param {object|null} data Vehicle data object.
		 */
		setInitialVehicleData( data ) {
			this.initialVehicleData = data && typeof data === 'object' ? data : null;
		},

		/**
		 * Update form field
		 */
		updateField( field, value ) {
			this[ field ] = value;
			// Clear error for this field
			if ( this.errors[ field ] ) {
				delete this.errors[ field ];
			}
			// Clear companyName error when bedrijfsnaam is updated (different key in validation)
			if ( field === 'bedrijfsnaam' && this.errors.companyName ) {
				delete this.errors.companyName;
			}
		},

		/**
		 * Update calculation parameters
		 */
		updateCalculation( params ) {
			if ( params.looptijd !== undefined ) {
				this.looptijd = params.looptijd;
			}
			if ( params.aanbetaling !== undefined ) {
				this.aanbetaling = params.aanbetaling;
			}
			if ( params.slottermijn !== undefined ) {
				this.slottermijn = params.slottermijn;
			}
			if ( params.inruilen !== undefined ) {
				this.inruilen = params.inruilen;
			}
			if ( params.type !== undefined ) {
				// Detect type change to show aankoopbedrag loader
				if ( params.type !== this.type ) {
					this.isAankoopbedragUpdating = true;
				}
				this.type = params.type;
			}
			if ( params.submissionMode !== undefined ) {
				this.submissionMode = params.submissionMode;
			}

			if ( params.kenteken !== undefined ) {
				this.kenteken = params.kenteken;
			}
			if ( params.kentekenInputMode !== undefined ) {
				this.kentekenInputMode = params.kentekenInputMode;
			}
		},

		setSubmissionMode( mode ) {
			this.submissionMode = mode === 'koop' ? 'koop' : 'lease';
		},

		/**
		 * Apply a vehicle data payload (REST `data` object or inlined equivalent)
		 * to the store. Pure state assignment, no I/O.
		 *
		 * @param {object} data Vehicle data object.
		 */
		applyVehicleData( data ) {
			if ( ! data || typeof data !== 'object' ) {
				return;
			}
			this.aankoopbedrag = Number( data.aankoopbedrag || 0 );
			if ( typeof data.btwBedrag !== 'undefined' ) {
				this.btwBedrag = Number( data.btwBedrag );
			}
			if ( typeof data.btwStatus !== 'undefined' ) {
				// Accept 'in', 'ex', or 'Marge' as valid BTW status values
				if ( data.btwStatus === 'in' || data.btwStatus === 'ex' || data.btwStatus === 'Marge' ) {
					this.btwStatus = data.btwStatus;
				} else {
					// Fallback: treat as 'ex' if unknown value
					this.btwStatus = 'ex';
				}
			}
			// Set title, image, and tags
			this.carTitle = data.title || 'Auto';
			this.carImageUrl = data.imageUrl || '';
			this.carImageSummaryUrl = data.imageUrlSummary || data.imageUrl || '';
			this.carTags = Array.isArray( data.tags ) ? data.tags : [];
			// Set lease data availability
			this.hasLeaseData = data.hasLeaseData === true;

			// Set feed lease values as defaults if available and current values are still defaults
			// This ensures feed maandbedrag can be used when looptijd + aanbetaling + slottermijn match
			if ( data.looptijd && ( this.looptijd === 72 || this.looptijd === null ) ) {
				this.looptijd = Number( data.looptijd );

				// Set aanbetaling from feed if available and not already set
				if ( data.aanbetaling !== undefined && data.aanbetaling !== null && this.aanbetaling === null ) {
					this.aanbetaling = Math.round( Number( data.aanbetaling ) );
				}

				// Set slottermijn from feed if available and not already set
				if ( data.slottermijn !== undefined && data.slottermijn !== null && this.slottermijn === null ) {
					this.slottermijn = Math.round( Number( data.slottermijn ) );
				}
			}

			// Reset aankoopbedrag updating state after fetch
			this.isAankoopbedragUpdating = false;
		},

		/**
		 * Fetch car data (placeholder capable).
		 *
		 * Uses the vehicle payload inlined into the page when present, avoiding a
		 * second browser round-trip for data the detail page already rendered
		 * server-side. Falls back to the REST endpoint otherwise.
		 */
		async fetchCarData() {
			if ( ! this.carId && ! this.vehicleUlid ) {
				return;
			}
			// Fast path: data inlined via the data-vehicle attribute.
			if ( this.initialVehicleData ) {
				this.applyVehicleData( this.initialVehicleData );
				return;
			}
			try {
				const response = this.vehicleUlid
					? await wordpressApi.getAanbodNewVehicle( this.vehicleUlid )
					: await wordpressApi.getCar( this.carId );
				if ( response && response.success && response.data ) {
					this.applyVehicleData( response.data );
				}
			} catch ( error ) {
				console.error( 'Fetch car data error:', error );
			}
		},

		/**
		 * Mark step as completed
		 */
		markStepCompleted( stepNumber ) {
			if ( ! this.completedSteps.includes( stepNumber ) ) {
				this.completedSteps.push( stepNumber );
			}
		},

		/**
		 * Calculate pricing
		 */
		async calculate() {
			if ( ! this.carId && ! this.vehicleUlid ) {
				return;
			}

			// Ensure type and looptijd are set (required for calculation)
			if ( this.type === null || this.looptijd === null ) {
				return;
			}

			// Keep null values as null to distinguish between "not set" and "explicitly set to 0"
			// This allows the API to use feed data when both are null, but calculate when explicitly set to 0
			const aanbetaling = this.aanbetaling;
			const slottermijn = this.slottermijn;

			this.errors = {};

			// Claim the latest request id; only this response may apply its result.
			const requestId = ++calculateRequestId;

			this.isPriceUpdating = true;

			try {
				const calculationParams = {
					car_id: this.carId || 0,
					looptijd: this.looptijd,
					aanbetaling: aanbetaling,
					slottermijn: slottermijn,
					inruilen: this.inruilen,
					type: this.type,
				};

				if ( this.vehicleUlid ) {
					calculationParams.vehicle_ulid = this.vehicleUlid;
				}
				
				const response = await wordpressApi.calculate( calculationParams );

				// A newer calculate() started while this was in flight: drop this
				// stale response so it can't overwrite the newer result.
				if ( requestId !== calculateRequestId ) {
					return;
				}

				if ( response.success ) {
					const calculated_maandbedrag = response.data.maandbedrag;
					
					// Validate result is positive
					if ( calculated_maandbedrag < 0 ) {
						throw new Error( 'Berekening resulteerde in een negatief maandbedrag. Controleer de ingevoerde waarden.' );
					}
					this.maandbedrag = calculated_maandbedrag;
				// Update BTW-status en BTW-bedrag uit response (dynamisch bij type wijziging)
				if ( typeof response.data.btwStatus !== 'undefined' ) {
					// Accept 'in', 'ex', or 'Marge' as valid BTW status values
					if ( response.data.btwStatus === 'in' || response.data.btwStatus === 'ex' || response.data.btwStatus === 'Marge' ) {
						this.btwStatus = response.data.btwStatus;
					} else {
						// Fallback: treat as 'ex' if unknown value
						this.btwStatus = 'ex';
					}
				}
					if ( typeof response.data.btwBedrag !== 'undefined' ) {
						this.btwBedrag = Number( response.data.btwBedrag );
					}
					// Update aankoopbedrag als deze is veranderd (bij type wijziging)
					if ( typeof response.data.aankoopbedrag !== 'undefined' ) {
						this.aankoopbedrag = Number( response.data.aankoopbedrag );
						// Reset aankoopbedrag updating state after update
						this.isAankoopbedragUpdating = false;
					}

					// Debug logging: log alle berekeningsparameters
					// const typeLabel = this.type === 1 ? 'Particulier' : 'Zakelijk';
					// const annualInterestRate = response.data.annual_interest_rate !== undefined ? response.data.annual_interest_rate : 'N/A';
					// const rentevoetSource = response.data.rentevoet_source || 'N/A';
					// const looptijd = response.data.looptijd !== undefined ? response.data.looptijd : 'N/A';
					// const aanbetaling = response.data.aanbetaling !== undefined ? response.data.aanbetaling : 0;
					// const slottermijn = response.data.slottermijn !== undefined ? response.data.slottermijn : 0;
					// const aankoopbedrag = response.data.aankoopbedrag !== undefined ? response.data.aankoopbedrag : 'N/A';
					// const netPrice = response.data.net_price !== undefined ? response.data.net_price : 'N/A';
					// const adjustedCapitalCost = response.data.adjusted_capital_cost !== undefined ? response.data.adjusted_capital_cost : null;
					// const monthlyDepreciation = response.data.monthly_depreciation !== undefined ? response.data.monthly_depreciation : null;
					// const monthlyFinanceCharge = response.data.monthly_finance_charge !== undefined ? response.data.monthly_finance_charge : null;
					// const calculatedMaandbedrag = response.data.calculated_maandbedrag !== undefined ? response.data.calculated_maandbedrag : null;
					// const fromFeed = response.data.from_feed || false;

					// const formatCurrency = ( value ) => {
					// 	if ( value === null || value === undefined || value === 'N/A' ) {
					// 		return 'N/A (not calculated)';
					// 	}
					// 	if ( typeof value === 'number' ) {
					// 		return `€${ value.toFixed( 2 ) }`;
					// 	}
					// 	return value;
					// };

					// console.log( `[RENTEVOET DEBUG] ========================================` );
					// console.log( `Type: ${ typeLabel } (${ this.type })` );
					// console.log( `Rentevoet: ${ annualInterestRate }% (Source: ${ rentevoetSource })` );
					// console.log( `Looptijd: ${ looptijd } maanden` );
					// console.log( `Aankoopbedrag: ${ formatCurrency( aankoopbedrag ) }` );
					// console.log( `Net Price: ${ formatCurrency( netPrice ) }` );
					// console.log( `Aanbetaling: ${ formatCurrency( aanbetaling ) }` );
					// console.log( `Slottermijn: ${ formatCurrency( slottermijn ) }` );
					// console.log( `Adjusted Capital Cost: ${ formatCurrency( adjustedCapitalCost ) }` );
					// console.log( `Monthly Depreciation: ${ formatCurrency( monthlyDepreciation ) }` );
					// console.log( `Monthly Finance Charge: ${ formatCurrency( monthlyFinanceCharge ) }` );
					// if ( fromFeed && calculatedMaandbedrag !== null ) {
					// 	console.log( `Maandbedrag (uit feed): €${ calculated_maandbedrag.toFixed( 2 ) }` );
					// 	console.log( `Maandbedrag (berekend): €${ calculatedMaandbedrag.toFixed( 2 ) }` );
					// 	const verschil = Math.abs( calculated_maandbedrag - calculatedMaandbedrag );
					// 	console.log( `Verschil: €${ verschil.toFixed( 2 ) }` );
					// } else {
					// 	console.log( `Maandbedrag: €${ calculated_maandbedrag.toFixed( 2 ) }` );
					// }
					// console.log( `From Feed: ${ fromFeed ? 'Yes (direct from feed)' : 'No (calculated)' }` );
					// console.log( `========================================` );
				} else {
					throw new Error( response.message || 'Calculation failed' );
				}
			} catch ( error ) {
				// Ignore errors from a superseded request.
				if ( requestId !== calculateRequestId ) {
					return;
				}
				this.errors.calculation = error.message || 'Er is een fout opgetreden bij het berekenen.';
				this.maandbedrag = 0;
				console.error( 'Calculation error:', error );
			} finally {
				// Only the latest request clears the updating state; an older one
				// finishing must not hide the spinner of a newer in-flight request.
				if ( requestId === calculateRequestId ) {
					this.isPriceUpdating = false;
				}
			}
		},

		/**
		 * Submit form
		 */
			async submit() {
			if ( ! this.isStep1Valid ) {
				this.errors.submit = 'Vul alle verplichte velden in.';
				return false;
			}

			// Validate step 2 on client before sending
			const clientErrors = this.step2ValidationErrors;
			if ( Object.keys( clientErrors ).length ) {
				this.errors = { ...this.errors, ...clientErrors };
				return false;
			}

			this.isSubmitting = true;
			this.errors = {};

				try {
					const payload = this.getSubmissionPayload();
					const response = await wordpressApi.submit( payload );

					// Success (200 with success true)
					if ( response && response.success ) {
						// Track request completed event for GTM
						const requestType = this.submissionMode === 'koop' ? 'buy' : 'lease';
						const pageType = 'zakelijk';
						trackRequestCompleted( requestType, {
							car_id: this.carId,
							submission_mode: this.submissionMode,
							entry_id: response.data?.entry_id || null,
							pagetype: pageType,
						} );
						
						// Check for redirect URL
						const redirect_url = response.data?.redirect_url || response.redirect_url;
						
						if ( redirect_url ) {
							// Full page redirect to thank you page
							window.location.href = redirect_url;
							return true;
						}
						
						// Fallback: set submitted state (should not happen if redirect_url is set)
						this.isSubmitted = true;
						return true;
					}

					// Map validation errors from API
					// WP_Error shape: { code, message, data: { status, errors: {..} } }
					if ( response && response.data && response.data.errors && typeof response.data.errors === 'object' ) {
						this.errors = { ...this.errors, ...response.data.errors };
						return false;
					}

					// Alternate shape
					if ( response && response.errors && typeof response.errors === 'object' ) {
						this.errors = { ...this.errors, ...response.errors };
						return false;
					}

					this.errors.submit = ( response && ( response.message || response.error ) ) ? ( response.message || response.error ) : 'Er is een fout opgetreden bij het verzenden.';
					return false;
				} catch ( error ) {
					this.errors.submit = error.message || 'Er is een fout opgetreden bij het verzenden.';
					console.error( 'Submit error:', error );
					return false;
				} finally {
				this.isSubmitting = false;
			}
		},

		/**
		 * Reset form
		 */
		reset() {
			this.type = 0;
			this.looptijd = 72;
			this.aanbetaling = null;
			this.slottermijn = null;
			this.inruilen = false;
			this.kenteken = '';
			this.naam = '';
			this.email = '';
			this.telefoonnummer = '';
			this.bedrijfsnaam = '';
			this.maandbedrag = 0;
			this.aankoopbedrag = 0;
			this.btwBedrag = 0;
			this.btwStatus = 'ex';
			this.completedSteps = [];
			this.errors = {};
			this.isSubmitted = false;
			this.isPriceUpdating = false;
			this.isAankoopbedragUpdating = false;
		},
	},
} );

